export type WebComponentState =
  | "pending"
  | "defined"
  | "loading"
  | "loaded"
  | "failed"
  | "external";

export interface WebComponentRegistryStatus {
  tags: { [tag: string]: WebComponentState };
  observing: boolean;
}

export interface WebComponentLoader {
  getStatus(): WebComponentRegistryStatus;
  stop(): void;
}

declare global {
  interface Window {
    __doWebComponentRegistry?: WebComponentLoader;
  }
}

type StateMap = { [key: string]: WebComponentState };
type StringMap = { [key: string]: string };

const DATA_BLOCK_ID = "do-web-component-registry";
const LOG_PREFIX = "[web-component-registry] ";
const TAG_PATTERN = /^[a-z][a-z0-9_]*-[a-z0-9_-]*$/;
const URL_PATTERN = /^(https:\/\/|\/)[^\s#\\]+$/i;
const OBSERVE_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
};

function warn(message: string): void {
  console.warn(LOG_PREFIX + message);
}

function urlKey(src: string): string {
  try {
    const url = new URL(src, document.baseURI);
    url.hash = "";
    url.searchParams.delete("v");
    return url.href;
  } catch {
    return "";
  }
}

function readRegistry(): StringMap {
  const components: StringMap = {};
  const block = document.getElementById(DATA_BLOCK_ID);
  if (!block) {
    warn("no #" + DATA_BLOCK_ID + " data block");
    return components;
  }

  let configured: unknown;
  try {
    configured = JSON.parse(block.textContent || "").components;
  } catch {
    warn("invalid registry JSON");
    return components;
  }

  if (!configured || typeof configured !== "object") {
    warn("registry has no components");
    return components;
  }

  const entries = configured as { [tag: string]: unknown };
  const tags = Object.keys(entries);
  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i];
    const url = entries[tag];
    if (
      TAG_PATTERN.test(tag) &&
      typeof url === "string" &&
      URL_PATTERN.test(url) &&
      urlKey(url)
    ) {
      components[tag] = url;
    } else {
      warn("skipping invalid entry " + tag);
    }
  }
  return components;
}

function isAlreadyRequested(key: string): boolean {
  const scripts = document.querySelectorAll("script[src]");
  for (let i = 0; i < scripts.length; i++) {
    if (urlKey((scripts[i] as HTMLScriptElement).src) === key) {
      return true;
    }
  }

  const entries =
    typeof performance !== "undefined" && performance.getEntriesByType
      ? performance.getEntriesByType("resource")
      : [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i] as PerformanceResourceTiming;
    if (entry.initiatorType === "script" && urlKey(entry.name) === key) {
      return true;
    }
  }
  return false;
}

export function startWebComponentLoader(): WebComponentLoader {
  const existing = window.__doWebComponentRegistry;
  if (existing) {
    return existing;
  }

  const components = readRegistry();
  const tags = Object.keys(components);
  const keys: StringMap = {};
  const states: StateMap = {};
  const urlStates: StateMap = {};
  let pendingCount = 0;
  let selector = "";
  let observer: MutationObserver | null = null;
  let originalAttachShadow: typeof Element.prototype.attachShadow | null = null;
  let attachShadowWrapper: typeof Element.prototype.attachShadow | null = null;
  let watchedRoots: WeakSet<Node>;

  function setState(tag: string, state: WebComponentState): void {
    if (states[tag] === "pending") {
      pendingCount--;
      selector = "";
    }
    states[tag] = state;
  }

  function settle(key: string, state: WebComponentState): void {
    urlStates[key] = state;
    for (let i = 0; i < tags.length; i++) {
      if (keys[tags[i]] === key && states[tags[i]] !== "defined") {
        setState(tags[i], state);
      }
    }
  }

  function loadScript(tag: string, key: string): void {
    const url = components[tag];
    const script = document.createElement("script");
    script.type = "module";
    script.src = url;
    script.setAttribute("data-web-component-registry", tag);
    script.onload = function () {
      settle(key, "loaded");
    };
    script.onerror = function () {
      settle(key, "failed");
      warn("failed to load " + url + " for <" + tag + ">");
    };
    settle(key, "loading");
    (document.head || document.documentElement).appendChild(script);
  }

  function trigger(tag: string): void {
    if (states[tag] !== "pending") {
      return;
    }

    const key = keys[tag];
    if (customElements.get(tag)) {
      setState(tag, "defined");
    } else if (urlStates[key]) {
      setState(tag, urlStates[key]);
    } else if (isAlreadyRequested(key)) {
      settle(key, "external");
    } else {
      loadScript(tag, key);
    }
  }

  function scan(root: ParentNode): void {
    if (!pendingCount) {
      return;
    }
    if (!selector) {
      selector = tags
        .filter(function (tag) {
          return states[tag] === "pending";
        })
        .join(",");
    }
    const found = root.querySelectorAll(selector);
    for (let i = 0; i < found.length && pendingCount; i++) {
      trigger(found[i].localName);
    }
  }

  function watchRoot(root: Document | ShadowRoot): void {
    if (!observer || watchedRoots.has(root)) {
      return;
    }
    watchedRoots.add(root);
    observer.observe(root, OBSERVE_OPTIONS);
    scan(root);
    if (!pendingCount) {
      return;
    }

    const elements = root.querySelectorAll("*");
    for (let i = 0; i < elements.length; i++) {
      const shadowRoot = elements[i].shadowRoot;
      if (shadowRoot) {
        watchRoot(shadowRoot);
      }
    }
  }

  function visit(element: Element): void {
    if (states[element.localName] === "pending") {
      trigger(element.localName);
    }
    if (element.firstElementChild) {
      scan(element);
    }
    if (element.shadowRoot) {
      watchRoot(element.shadowRoot);
    }
  }

  function disconnect(): void {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (
      attachShadowWrapper &&
      Element.prototype.attachShadow === attachShadowWrapper
    ) {
      Element.prototype.attachShadow = originalAttachShadow;
    }
  }

  function onMutations(records: MutationRecord[]): void {
    for (let i = 0; i < records.length && pendingCount; i++) {
      const added = records[i].addedNodes;
      for (let j = 0; j < added.length && pendingCount; j++) {
        if (added[j].nodeType === 1) {
          visit(added[j] as Element);
        }
      }
    }
    if (!pendingCount) {
      disconnect();
    }
  }

  function installAttachShadowWrapper(): void {
    const original = Element.prototype.attachShadow;
    if (!original) {
      return;
    }
    originalAttachShadow = original;
    attachShadowWrapper = function (this: Element, init: ShadowRootInit) {
      const root = original.call(this, init);
      if (observer && init && init.mode === "open") {
        try {
          watchRoot(root);
        } catch {
          // A failure to observe must never break the caller's attachShadow.
        }
      }
      return root;
    };
    Element.prototype.attachShadow = attachShadowWrapper;
  }

  const api: WebComponentLoader = {
    getStatus: function () {
      const snapshot: StateMap = {};
      for (let i = 0; i < tags.length; i++) {
        snapshot[tags[i]] = states[tags[i]];
      }
      return { tags: snapshot, observing: !!observer };
    },
    stop: function () {
      disconnect();
      if (window.__doWebComponentRegistry === api) {
        delete window.__doWebComponentRegistry;
      }
    },
  };
  window.__doWebComponentRegistry = api;

  if (typeof customElements === "undefined" || !window.MutationObserver) {
    return api;
  }

  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i];
    keys[tag] = urlKey(components[tag]);
    if (customElements.get(tag)) {
      states[tag] = "defined";
    } else {
      states[tag] = "pending";
      pendingCount++;
    }
  }

  if (pendingCount) {
    watchedRoots = new WeakSet<Node>();
    observer = new MutationObserver(onMutations);
    installAttachShadowWrapper();
    watchRoot(document);
    if (!pendingCount) {
      disconnect();
    }
  }

  return api;
}
